import { Button, Col, Form, Input, Row, Select } from 'antd'
import OrganizationInput from 'components/global/inputs/OrganizationInput/OrganizationInput'
import React, { useEffect, useMemo, useState } from 'react'
import { Address, OrganizationResp, Phone, Photo } from 'types/globalTypes'
import Location from 'pages/Events/Views/CreateCommunity/Screens/Step1/InputComps/Location'
import {
  formValidationOnRequiredFields,
  validatePhone,
  validateUrl,
} from 'helper/utility'
import PhoneInput from 'react-phone-input-2'
import UploadDocument from 'components/global/UploadDocument'
import { Link, useHistory } from 'react-router-dom'
import { infoMessage } from 'api/global'
import { useCreateExternal, useUpdateExternal } from 'api/External/External'
import { externalStore } from 'state/event/external'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { ExternalEventResp } from 'types/external/externalEvent'

interface ExternalStep1Props {
  handleNext: () => void
}

const ExternalStep1: React.FC<ExternalStep1Props> = ({ handleNext }) => {
  const history = useHistory()
  const [form] = Form.useForm()
  const {
    externalData,
    setExternalData,
  }: {
    externalData: ExternalEventResp
    setExternalData: React.Dispatch<React.SetStateAction<ExternalEventResp>>
  } = externalStore(store => ({
    externalData: store.externalData,
    setExternalData: store.setExternalData,
  }))

  const [selectedOrganization, setSelectedOrganization] = useState<
    OrganizationResp | undefined
  >()
  const [documentIds, setDocumentIds] = useState<string[]>([])
  const [photos, setPhotos] = useState<Photo[]>([])
  const [mapData, setMapModalData] = useState<Address | undefined>()
  const [venueType, setVenueType] = useState<any>('physical')
  const [phone, setPhone] = useState<Phone>({
    dialCode: externalData ? externalData.phone?.dialCode : '',
    isoCode: externalData ? externalData.phone?.isoCode.toLowerCase() : 'us',
    number: externalData ? externalData.phone?.number : '',
  })

  const { mutate: createExternal, isLoading: createExternalLoading } =
    useCreateExternal()
  const { mutate: updateExternal, isLoading: updateExternalLoading } =
    useUpdateExternal()

  const handleSubmit = (values: {
    name: string
    websiteUrl: string
    registrationLink: string
  }) => {
    if (!selectedOrganization) return infoMessage('Organization is required')
    if (!formValidationOnRequiredFields(values, ['name', 'websiteUrl'])) return
    if (venueType === 'physical' && !mapData)
      return infoMessage('Location is required')
    if (!documentIds || !documentIds.length)
      return infoMessage('Image is required')
    if (phone.number && phone.number.length > 3 && phone.number.length < 10)
      return infoMessage('Invalid phone number')

    const payload = {
      ...values,
      [mapData?._id ? 'locationId' : 'location']:
        venueType === 'physical'
          ? mapData._id
            ? mapData._id
            : mapData
          : undefined,
      coverPhotosIds: documentIds.length > 0 ? documentIds : undefined,
      organizationId: selectedOrganization._id,
      phone: phone.number && phone.number.length > 4 ? phone : undefined,
      venueType: venueType,
    }

    if (externalData) {
      updateExternal(
        { payload, externalId: externalData._id },
        {
          onSuccess: data => {
            setExternalData({ ...externalData, ...data })
            handleNext()
          },
        },
      )
    } else {
      createExternal(payload, {
        onSuccess: data => {
          setExternalData(data)
          handleNext()
        },
      })
    }
  }

  if (externalData) {
    form.setFieldsValue({
      phone: externalData.phone?.number,
    })
  }

  useEffect(() => {
    if (externalData) {
      setSelectedOrganization(externalData.organization)
      setMapModalData(externalData.location)
      setDocumentIds(externalData.coverPhotosIds || [])
      setPhotos(externalData.coverPhotos || [])
      setVenueType(externalData.venueType)

      form.setFieldsValue({
        name: externalData.name,
        websiteUrl: externalData.websiteUrl,
        phone: phone?.number,
        registrationLink: externalData.registrationLink,
        venueType: externalData.venueType,
      })
    }
  }, [externalData, phone])

  return (
    <div className="create-event-main-content-box" style={{ margin: '0 auto' }}>
      <h2>Step 1. External’s settings</h2>
      <Form
        form={form}
        autoComplete="off"
        layout="vertical"
        onFinish={handleSubmit}
        // initialValues={{
        //   name: externalData?.name,
        //   websiteUrl: externalData?.websiteUrl,
        //   phone: externalData?.phone?.number,
        //   customUrl: externalData?.customUrl,
        //   venueType: externalData?.venueType || 'physical',
        // }}
        // key={externalData?._id || 'new-form'}
      >
        <OrganizationInput
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
        />
        <Row>
          <Col>
            <label className="input-label required-input">External name</label>
          </Col>
          <Col>
            <Form.Item className="form-control" name="name">
              <Input
                placeholder="External name"
                className="form-control-field"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col>
            <label className="input-label required-input">Venue type</label>
          </Col>
          <Col>
            <Select
              className="h-50px select-field w-full"
              placeholder="Select"
              value={venueType}
              onChange={(v, item: any) => {
                setVenueType(item.key)
                form.setFieldsValue({
                  gender: item.key,
                })
              }}
            >
              <Select.Option key={'physical'} value={'physical'}>
                In-person
              </Select.Option>
              <Select.Option key={'virtual'} value={'virtual'}>
                Online
              </Select.Option>
              <Select.Option
                key={'to-be-determined'}
                value={'to-be-determined'}
              >
                To be decided
              </Select.Option>
            </Select>
          </Col>
        </Row>
        {selectedOrganization && venueType === 'physical' && (
          <Row className="mt-5px">
            <Col span={24}>
              <Location
                selectedOrganization={selectedOrganization}
                mapData={mapData}
                setMapModalData={setMapModalData}
                form={form}
              />
            </Col>
          </Row>
        )}
        <Row className="mt-10px">
          <Col span={24}>
            <label className="input-label">Phone number</label>
            <Form.Item name="phone" className="phone-number-form">
              <PhoneInput
                country={phone?.isoCode?.toLowerCase() || 'us'}
                onChange={(value: string, data: any) => {
                  setPhone({
                    dialCode: '+' + data?.dialCode,
                    number: '+' + value,
                    isoCode: data.countryCode.toUpperCase(),
                  })
                }}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <label htmlFor="" className="input-label required-input">
              Website
            </label>
            <Form.Item
              colon={false}
              name="websiteUrl"
              className="form-control"
              rules={[{ validator: validateUrl }]}
            >
              <Input
                placeholder="Enter Website URL"
                name="websiteUrl"
                className="form-control-field"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24}>
            <label htmlFor="" className="input-label">
              Registration URL
            </label>
            <Form.Item
              colon={false}
              name="registrationLink"
              className="form-control"
              rules={[{ validator: validateUrl }]}
            >
              <Input
                placeholder="Custom Url"
                name="registrationLink"
                className="form-control-field"
              />
            </Form.Item>
          </Col>
        </Row>
        <Row className="mt-10px">
          <Col span={18}>
            <label className="input-label required-input">Image</label>
            <UploadDocument
              documentIds={documentIds}
              setDocumentIds={setDocumentIds}
              multiple={true}
              docType="image/*"
              files={photos}
              uploadMultipleAtOnce={true}
            />
          </Col>
        </Row>

        <Row justify="space-between" className="mb-26 mt-40px">
          <Button
            className="cancel-button"
            onClick={() => history.push(`/spaces?page=1&limit=10`)}
          >
            Back to listing
          </Button>

          <Button
            className="stepper-button"
            disabled={createExternalLoading || updateExternalLoading}
            htmlType="submit"
          >
            {createExternalLoading || updateExternalLoading ? (
              <ButtonSpinner size={20} color="#000" />
            ) : (
              'Next'
            )}
          </Button>
        </Row>
      </Form>
    </div>
  )
}

export default ExternalStep1
